import axios from 'axios';
import { createAsyncThunk, isFulfilled, isPending, isRejected } from '@reduxjs/toolkit';
import { cleanEntity } from '../shared/util/entity-utils/entity-utils';
import { createEntitySlice, EntityState, getAllRequest, serializeAxiosError } from '../shared/reducers/reducer.utils';

const apiUrl = 'api/profase-workflow-actividads';

function extractErrorMessage(error: any, fallback: string): string {
    const data = error?.response?.data;
    return data?.detail ?? data?.message ?? data?.title ?? error?.message ?? fallback;
}

export const getEntities = createAsyncThunk(
    'workflowActividad/fetch_entity_list',
    async (request: any) => {
        const params = getAllRequest(request);
        return axios.get<any[]>(`${apiUrl}?${params}&cacheBuster=${new Date().getTime()}`);
    },
    { serializeError: serializeAxiosError },
);

export const getEntity = createAsyncThunk(
    'workflowActividad/fetch_entity',
    async (id: string | number) => {
        return axios.get<any>(`${apiUrl}/${id}`);
    },
    { serializeError: serializeAxiosError },
);

export const createEntity = createAsyncThunk(
    'workflowActividad/create_entity',
    async (entity: any) => {
        return axios.post<any>(apiUrl, cleanEntity(entity));
    },
    { serializeError: serializeAxiosError },
);

export const updateEntity = createAsyncThunk(
    'workflowActividad/update_entity',
    async (entity: any) => {
        return axios.put<any>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    },
    { serializeError: serializeAxiosError },
);

export const deleteEntity = createAsyncThunk(
    'workflowActividad/delete_entity',
    async (id: string | number) => {
        return axios.delete<any>(`${apiUrl}/${id}`);
    },
    { serializeError: serializeAxiosError },
);

export const WorkflowActividadSlice = createEntitySlice({
    name: 'workflowActividad',
    skipRejectionHandling: true,
    initialState: {
        loading: false,
        errorMessage: null,
        entities: [],
        entity: {},
        updating: false,
        totalItems: 0,
        updateSuccess: false,
    } as EntityState<any>,
    extraReducers(builder) {
        builder
            .addCase(getEntity.fulfilled, (state, action) => {
                state.loading = false;
                state.entity = action.payload.data;
            })
            .addCase(deleteEntity.fulfilled, state => {
                state.updating = false;
                state.updateSuccess = true;
                state.entity = {};
            })
            .addMatcher(isFulfilled(getEntities), (state, action) => {
                const { data, headers } = action.payload;
                return {
                    ...state,
                    loading: false,
                    entities: data,
                    totalItems: parseInt(headers['x-total-count'], 10),
                };
            })
            .addMatcher(isFulfilled(createEntity, updateEntity), (state, action) => {
                state.updating = false;
                state.loading = false;
                state.updateSuccess = true;
                state.entity = action.payload.data;
            })
            .addMatcher(isPending(getEntities, getEntity), state => {
                state.errorMessage = null;
                state.updateSuccess = false;
                state.loading = true;
            })
            .addMatcher(isPending(createEntity, updateEntity, deleteEntity), state => {
                state.errorMessage = null;
                state.updateSuccess = false;
                state.updating = true;
            })
            .addMatcher(isRejected(getEntities, getEntity), (state, action) => {
                state.loading = false;
                state.updateSuccess = false;
                state.errorMessage = extractErrorMessage(action.error, 'Error al cargar los datos');
            })
            .addMatcher(isRejected(createEntity, updateEntity, deleteEntity), (state, action) => {
                state.updating = false;
                state.loading = false;
                state.updateSuccess = false;
                state.errorMessage = extractErrorMessage(action.error, 'Error al procesar la solicitud');
            });
    },
});

export const { reset } = WorkflowActividadSlice.actions;
export default WorkflowActividadSlice.reducer;
